import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { useAuth } from '../packages/auth-client';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';

/**
 * ConfirmEmailChangePage — public endpoint reachable from the link in the
 * new mailbox. POSTs the token to /users/confirm-email-change. On success
 * the backend has revoked every refresh token the user holds; we clear the
 * local session and bounce to /login so they sign in with the new address.
 *
 * Auto-submits on mount when a token is in the URL — the link the user
 * clicked is the credential, so making them press a button adds friction
 * without security benefit.
 */
export function ConfirmEmailChangePage() {
    usePageTitle('Confirm email change');
    const { theme } = useTheme();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const token = params.get('token') ?? '';
    const tokenMissing = token.trim() === '';

    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>(
        tokenMissing ? 'error' : 'submitting',
    );
    const [error, setError] = useState(
        tokenMissing ? 'This link is missing its confirmation token.' : '',
    );

    // Guard against React 18 StrictMode double-invoking the effect in dev,
    // which would otherwise consume the single-use token before the
    // success state can render.
    const fired = useRef(false);

    useEffect(() => {
        if (tokenMissing || fired.current) return;
        fired.current = true;
        (async () => {
            try {
                await apiClient.post('/users/confirm-email-change', { token });
                setStatus('success');
                // Tear down the now-orphaned local session — backend has
                // already revoked the refresh token, so any axios retry on
                // a stale access token would 401 anyway.
                try { await logout({ redirect: false }); } catch { /* logout is best-effort here */ }
                toast.success('Email updated. Please sign in with your new address.');
                setTimeout(() => navigate('/login', { replace: true }), 1500);
            } catch (err) {
                setError(getErrorMessage(err));
                setStatus('error');
            }
        })();
    }, [token, tokenMissing, navigate, logout]);

    return (
        <div className="login-container">
            <div className="login-card">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                <h1 className="login-title">Confirm email change</h1>

                {status === 'submitting' && (
                    <p className="login-subtitle">Confirming your new email address…</p>
                )}

                {status === 'success' && (
                    <p className="login-subtitle" style={{ color: 'var(--ok)' }}>
                        Your email address has been updated. Redirecting to sign in…
                    </p>
                )}

                {status === 'error' && (
                    <>
                        <p className="login-subtitle" style={{ color: 'var(--danger)' }}>
                            {error || 'This confirmation link is invalid or has expired.'}
                        </p>
                        <Link
                            to="/profile"
                            className="btn btn-primary form-button"
                            style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                        >
                            Back to profile
                        </Link>
                    </>
                )}
            </div>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                    Back to sign in
                </Link>
            </p>
        </div>
    );
}
